<template>
  <div class="col-12 mt-4 hr-result-container">
    <DottedLineHeader :text="$t('more.ecg') + (userECGTest ? (' - ' + $t('more.date') + ': ') : '')"  :value="userECGTest ? testDate : ''">
      <div class="d-flex toolbar-container">
        <MiniToolbar v-if="!printing" class="p-2" :buttons="toolbar" @clicked="toolbarClicked"/>

        <b-dropdown v-if="!disabledForRender && ecgTestsToSelect && ecgTestsToSelect.length > 1" id="dropdown-select" :text="$t('more.select_ecg_test')" class="m-md-2">
          <b-dropdown-item v-for="test in ecgTestsToSelect" :key="test.uuid" @click="onSelectTest(test)">
            {{jdate(test.tested_at) + ' - ' + test.name + ' - ' + test.description}}
          </b-dropdown-item>
        </b-dropdown>

      </div>
    </DottedLineHeader>

    <Loading v-if="ecgTestLoading"/>
    <Error v-if="ecgTestLoadError && ecgTestLoadError.status !== 404"/>
    <NoData v-if="!userECGTest || (!ecgTestData && !ecgTestLoading && (!ecgTestLoadError || ecgTestLoadError.status === 404))"/>

    <div v-if="lazy_chart_height !== filter_settings.chart_height" class="chart-height-message-container ml-4 mr-4">
      <span class="chart-height-message icon sli-refresh"></span>
      <span class="chart-height-message-caption">{{$t("more.chart_height_message")}}</span>
    </div>

    <div v-if="userECGTest && !ecgTestLoading && !ecgTestLoadError" class="row hrresult">
      <div class="col-12">
        <span class="ecgresult-title">{{$t('more.ecg_test_description')}}: </span><span class="ecgresult-value">{{testDescription}}</span>
      </div>
      <div class="col-md-4">
        <span class="ecgresult-title">{{$t('more.test_duration')}}: </span><span class="ecgresult-value">{{`${testDuration} ${$t('more.seconds')}`}}</span>
      </div>
      <div v-if="view_settings && (!view_settings.hasOwnProperty('calibrate') || parseInt(view_settings.calibrate) === 100)" class="col-12 calibrate-tip mt-3">
        <span class="icon sli-directions"></span>
        <span>{{$t('more.calibrate_message')}}</span>
      </div>
      <!-- before render will be emitted some where else -->
      <EcgCharts :data="data" :duration="testDuration" :mmPerMV="chartMmPerMv" :mmPerSec="chartMmPerSec" :chartHeight="lazy_chart_height" :monitorSize="view_settings.monitor_size" :monitorRatio="view_settings.monitor_ratio" :screenWidth="screenWidth" @afterRender="afterRender" :calibrateValue="view_settings.calibrate"/>
      <div class="col-12 hrresult-tags">
        <span>{{$t('more.tags')}}: </span><ul class="tc-tags">
          <li v-for="tag in tags" :key="tag" tabindex="0" class="tc-tag tc-valid"><div class="tc-content"><!----> <div class="tc-tag-center"><span class="">{{tag}}</span> <!----> </div> <!----></div></li>
        </ul>
      </div>
    </div>

    <b-modal id="modal-1" ref="modal" :title="$t('more.edit_tag')" :ok-title="$t('modal.ok')" :cancel-title="$t('modal.cancel')" @hidden="resetModel" @ok="handleEditTags">
        <div class="modal-body mx-3">
          <div class="form-group">
            <label for="tags_edit">{{ $t('more.annotation') }}</label>
            <vue-tags-input
              name="tags_edit"
              v-model="tag"
              :tags="editing_tags || tags"
              @tags-changed="newTags => editing_tags = newTags"
            />
          </div>
        </div>
    </b-modal>

    <b-modal id="modal-2" ref="modalSettings" :title="$t('more.settings')" :ok-title="$t('modal.ok')" :cancel-title="$t('modal.cancel')" @hidden="resetSettingsModel" @ok="handleSettings">
        <div class="modal-body mx-3">
            <b-tabs class="setting-tabs" v-model="settingTabIndex" pills card>
              <b-tab :title="$t('more.ecg_settings_filter')" active>
                <div class="form-check form-switch mb-4">
                  <input class="form-check-input" type="checkbox" id="filtersNotch" v-model="editing_filter_settings.notch" v-on:change="checkFilterSettings('notch', editing_filter_settings.notch)" :checked="editing_filter_settings.notch">
                  <label class="form-check-label" for="filtersNotch">{{$t("more.notch_filter")}}</label>
                  <span class="filter-desc">{{$t("more.notch_filter_desc")}}</span>
                </div>
                <div class="form-check form-switch mb-4">
                  <input class="form-check-input" type="checkbox" id="filtersLowPass" v-model="editing_filter_settings.low_pass" v-on:change="checkFilterSettings('low_pass', editing_filter_settings.low_pass)">
                  <label class="form-check-label" for="filtersLowPass">{{$t("more.low_pass_filter")}}</label>
                  <span class="filter-desc">{{$t("more.low_pass_filter_desc")}}</span>
                </div>
                <div class="form-check form-switch mb-4">
                  <input class="form-check-input" type="checkbox" id="filtersHighPass" v-model="editing_filter_settings.high_pass" v-on:change="checkFilterSettings('high_pass', editing_filter_settings.high_pass)">
                  <label class="form-check-label" for="filtersHighPass">{{$t("more.high_pass_filter")}}</label>
                  <span class="filter-desc">{{$t("more.high_pass_filter_desc")}}</span>
                </div>
                <div class="form-check form-switch mb-4">
                  <input class="form-check-input" type="checkbox" id="filtersImprovement" v-model="editing_filter_settings.improvement" v-on:change="checkFilterSettings('improvement', editing_filter_settings.improvement)">
                  <label class="form-check-label" for="filtersImprovement">{{$t("more.enable_improvement")}}</label>
                  <span class="filter-desc">{{$t("more.enable_improvement_desc")}}</span>
                </div>
                <div class="row mb-4">
                  <div class="col-5">
                    <div class="input-group">
                      <label for="slider-x">{{$t("more.scale_x")}}</label>
                      <input type="range" id="slider-x" name="slider-x" min="0" max="4" v-model="editing_filter_settings.scale_x" v-tippy :content="`${toMmPerSec(editing_filter_settings.scale_x)} ${$t('more.mm_per_sec')}`">
                    </div>
                  </div>
                  <div class="col-5">
                    <div class="input-group">
                      <label for="slider-y">{{$t("more.scale_y")}}</label>
                      <input type="range" id="slider-y" name="slider-y" min="0" max="4" v-model="editing_filter_settings.scale_y" v-tippy :content="`${toMmPerMv(editing_filter_settings.scale_y)} ${$t('more.mm_per_mv')}`">
                    </div>
                  </div>
                  <div class="col-5">
                    <div class="input-group">
                      <label for="slider-height">{{$t("more.chart_height")}}</label>
                      <input type="range" id="slider-height" name="slider-height" min="50" max="300" v-model="editing_filter_settings.chart_height" v-tippy :content="editing_filter_settings.chart_height">
                    </div>
                  </div>
                </div>
              </b-tab>
              <b-tab :title="$t('more.ecg_settings_view')">
                <div class="row mb-4">
                  <div class="col-6">
                    <div class="input-group">
                      <div class="form-check form-switch form-view-combo mb-4">
                        <label class="form-check-label" for="monitorSize">{{$t("more.monitor_size")}}</label>
                        <select name="monitorSize" id="monitorSize" v-model="editing_view_settings.monitor_size">
                          <option v-for="inch in Array.from(Array(29).keys())" :key="inch + 1" :value="inch + 1">{{inch + 1}}</option>
                        </select>
                        <span class="filter-desc">{{$t("more.monitor_size_desc")}}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="input-group">
                      <div class="form-check form-switch form-view-combo mb-4">
                        <label class="form-check-label" for="monitorRatio">{{$t("more.monitor_ratio")}}</label>
                        <select name="monitorRatio" id="monitorRatio" v-model="editing_view_settings.monitor_ratio">
                          <option v-for="ratio in screenRatioList" :key="ratio" :value="ratio">{{ratio}}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mb-4">
                  <div class="col-16">
                    <label>{{$t('more.calibrate_desc')}}:
                      <input id="calibrate-range" type="number" step="0.2" value="100" min="50" max="200" v-model="editing_view_settings.calibrate"/>
                    </label>
                    <canvas id="calibrate-canvas" :style="{width: '100%', height: '200px'} "></canvas>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
        </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState, mapActions } from 'vuex'
import PersianDate from 'persian-date'
import VueTagsInput from '@johmun/vue-tags-input'
import MiniToolbar from '@/components/MiniToolbar.vue'
import DefaultDatePickerOptions from '@/components/DefaultDatePickerOptions.js'
import DottedLineHeader from '@/components/DottedLineHeader.vue'
import GradientChart from '@/components/GradientChart.vue'
import NoData from '@/components/NoData.vue'
import Loading from '@/components/Loading.vue'
import Error from '@/components/Error.vue'
import EcgCharts from '@/components/EcgCharts.vue'
import { ecgFilterWorker } from '@/workers/ecgfilter.worker.js'
import grid from '@/_helpers/grid.js'

export default {
  components: {
    VueTagsInput,
    DottedLineHeader,
    GradientChart,
    MiniToolbar,
    EcgCharts,
    NoData,
    Loading,
    Error
  },
  props: {
    sharedTest: Object,
    printing: Boolean
  },
  data: () => ({
    data: [],
    resultFromDate: -1,
    resultToDate: -1,
    tag: '',
    editing_tags: null,
    editTagsSubmitted: false,
    filter_settings: {},
    view_settings: {},
    editing_filter_settings: {},
    editing_view_settings: {},
    unsubscribe: null,
    testStart: null,
    testEnd: null,
    testDuration: -1,
    lazy_chart_height: 120,
    screenHeight: -1,
    screenWidth: -1,
    screenRatioList: ['16:10', '16:9', '4:3'],
    grid: null,
    ecgTestData: null,
    ecgTestsToSelect: [],
    selectedEcgTest: null,
    disabledForRender: false,
    settingTabIndex: 0
  }),
  computed: {
    ...mapState({
      testMinimalItems: state => state.test.ecgtestsMinimal.items,
      resultFrom: state => state.test.testsMinimalFrom,
      ecgTestLoading: state => state.test.ecgTestData.loading,
      ecgTestLoadError: state => state.test.ecgTestData.error,
      ecgTests: state => state.test.ecgTests
    }),
    userECGTest: {
      get: function () {
        if (this.selectedEcgTest) {
          return this.selectedEcgTest
        }

        if (!this.sharedTest || !this.sharedTest.last_tests || this.sharedTest.last_tests.length === 0) {
          return null
        }
        var hrTest = this.sharedTest.last_tests.filter(test => {
          return test.type === 'ecg'
        })
        if (hrTest.length === 0) {
          return null
        }

        // select last test to show
        var maxTested = -1
        var lastTest = null
        hrTest.forEach(test => {
          if (test.tested_at > maxTested) {
            maxTested = test.tested_at
            lastTest = test
          }
        })
        return lastTest
      },
      set: function (test) {
        this.selectedEcgTest = test
        this.ecgTestsToSelect = []
      }
    },
    tags () {
      var test = this.userECGTest
      if (!test) {
        return []
      }
      return test.tags || []
    },
    testDate () {
      var test = this.userECGTest
      if (!test) {
        return ''
      }
      return this.jdate(test.tested_at)
    },
    testDescription () {
      var test = this.userECGTest
      if (!test) {
        return ''
      }
      return test.description
    },
    calendarOptions () {
      var opts = DefaultDatePickerOptions(false)
      opts['onSelect'] = this.onDateSelected
      opts['monthPicker'] = {
        'onSelect': this.onMonthSelected
      }
      opts['yearPicker'] = {
        'onSelect': this.onYearSelected
      }
      opts['navigator'] = {
        'onPrev': this.onPrev,
        'onNext': this.onNext
      }
      return opts
    },
    toolbar () {
      var result = []
      if (this.userECGTest) {
        result.push({
          icon: 'sli-settings',
          id: 'hrresult-settings',
          tip: 'more.settings',
          visible: !this.disabledForRender
        })
        result.push({
          icon: 'sli-clock',
          id: 'hrresult-history',
          tip: 'more.history',
          visible: !this.disabledForRender
        })
        result.push({
          icon: 'sli-tag',
          id: 'bpresult-tag',
          tip: 'more.edit_tag',
          small: true
        })
        result.push({
          icon: 'sli-bubbles',
          id: 'bpresult-discussion',
          tip: 'more.discussion',
          small: true
        })
        result.push({
          icon: 'sli-cloud-download',
          id: 'bpresult-download',
          tip: 'more.download_data',
          visible: this.can('shared.test', 'download'),
          small: true
        })
      }
      return result
    },
    filterFlags () {
      var flags = 0x00
      if (this.filter_settings.notch) {
        flags |= (1 << 2)
      }
      if (this.filter_settings.low_pass) {
        flags |= (1 << 1)
      }
      if (this.filter_settings.high_pass) {
        flags |= (1 << 3)
      }
      if (this.filter_settings.improvement) {
        flags |= (1 << 4)
        flags |= (1 << 0)
      }
      return flags
    },
    chartScaleX () {
      if (!this.filter_settings || !this.filter_settings.hasOwnProperty('scale_x') || (!this.filter_settings.scale_x && this.filter_settings.scale_x !== 0)) {
        return 1
      }
      return parseInt(this.filter_settings.scale_x) + 1
    },
    chartScaleY () {
      if (!this.filter_settings || !this.filter_settings.hasOwnProperty('scale_y') || (!this.filter_settings.scale_y && this.filter_settings.scale_y !== 0)) {
        return 1
      }
      return parseInt(this.filter_settings.scale_y) + 1
    },
    chartMmPerSec () {
      return this.toMmPerSec(this.chartScaleX - 1)
    },
    chartMmPerMv () {
      return this.toMmPerMv(this.chartScaleY - 1)
    }
  },
  watch: {
    editing_view_settings: {
      deep: true,
      handler () {
        this.renderCalibrate()
      }
    },
    settingTabIndex (to, from) {
      if (to === 1) {
        setTimeout(() => {
          this.renderCalibrate()
        }, 100)
      }
    }
  },
  methods: {
    ...mapActions('test', ['testsMinimal', 'tests', 'editTestTags', 'testData']),
    jdate (unix) {
      if (!unix) {
        return ''
      }
      return new PersianDate(unix * 1000).toLocale('fa').format('D MMMM HH:mm')
    },
    toMmPerSec (value) {
      return 3.125 * Math.pow(2, value)
    },
    toMmPerMv (value) {
      return 1.25 * Math.pow(2, value)
    },
    resetModel () {
      this.editTagsSubmitted = false
      this.editing_tags = this.tags
    },
    handleEditTags (bvModalEvt) {
      bvModalEvt.preventDefault()
      this.editTagsSubmitted = true
      this.$validator.validate().then(valid => {
        if (valid) {
          this.$refs.modal.hide()
          var tags = this.editing_tags.map(tag => {
            return tag.text
          })

          var test = this.userECGTest
          if (!test) {
            return
          }
          this.editTestTags({testUuid: test.uuid, tags: tags})
        }
      })
    },
    resetSettingsModel () {
      this.editing_filter_settings = Object.assign({}, this.filter_settings)
      this.editing_view_settings = Object.assign({}, this.view_settings)
    },
    handleSettings (bvModalEvt) {
      // request data from server again!
      this.requestData()
      this.filter_settings = Object.assign({}, this.editing_filter_settings)
      this.view_settings = Object.assign({}, this.editing_view_settings)
      localStorage.setItem('filter_settings', JSON.stringify(this.filter_settings))
      localStorage.setItem('view_settings', JSON.stringify(this.view_settings))
    },
    toolbarClicked: function (id, button) {
      switch (button.icon) {
        case 'sli-settings':
          this.$refs.modalSettings.show()
          break
        case 'sli-clock':
          // datepicker will show up and no need to write code here
          break
        case 'sli-tag':
          this.editing_tags = this.tags
          this.$refs.modal.show()
          break
        case 'sli-bubbles':
          this.$emit('openDicussion', this.userECGTest.uuid, this.sharedTest.user)
          break
        case 'sli-cloud-download':
          const dataUrl = window.URL.createObjectURL(new Blob([JSON.stringify(this.ecgTestData)]))
          var a = document.createElement('a')
          a.href = dataUrl
          a.download = this.userECGTest.uuid
          a.click()
          break
      }
    },
    initDatePicker: function () {
      $('#hrresult-history').off('focus click')
      $('#hrresult-history').on('focus click', (evt) => {
        this.showDataPicker(evt)
      })
    },
    showDataPicker: function (evt) {
      if (!this.sharedTest) {
        return
      }

      $('div[id|="persianDateInstance"]').remove()
      $('#hrresult-history').off('focus click')
      $('#hrresult-history').pDatepicker(this.calendarOptions)
      $('#hrresult-history').trigger('click')
      this.initDatePicker()
      this.getHighlightedDays()
    },
    getHighlightedDays: function () {
      setTimeout(() => {
        var days = $('td[data-date]:not(:has(span[class=\'other-month\']))')
        if (!days) {
          return
        }
        var resultFrom = days[0].dataset.date
        this.resultFromDate = this.jalaliToTimeStamp(resultFrom)
        this.resultToDate = this.jalaliToTimeStamp(days[days.length - 1].dataset.date, false)
        this.testsMinimal({userUuid: this.sharedTest.user.uuid, fromDate: this.resultFromDate, toDate: this.resultToDate, type: 'ecg', resultFrom})
      }, 500)
    },
    jalaliToTimeStamp: function (date, start = true) {
      var x = date.match(/(\d{0,4}),(\d{0,2}),(\d{0,2})/)
      if (x.length < 4) {
        // date not valid
        return
      }
      x = x.map(function (e) {
        return parseInt(e, 10)
      })
      if (start) {
        return new PersianDate([x[1], x[2], x[3]]).startOf('day').format('X')
      } else {
        return new PersianDate([x[1], x[2], x[3]]).endOf('day').format('X')
      }
    },
    highlightDaysOnDatePicker (testMinimalItems) {
      if (!testMinimalItems) {
        return
      }
      var flags = testMinimalItems.value
      this.flags = flags
      var i = 0

      var x = this.resultFrom.match(/(\d{0,4}),(\d{0,2}),(\d{0,2})/)
      if (!x || x.length < 4) {
        // date not valid
        return
      }

      while (flags > 0) {
        if (flags & 1) {
          var date = `${x[1]},${x[2]},${i + 1}`
          $('td[data-date=\'' + date + '\'] span:first-child').addClass('highlighted-date')
        }

        flags /= 2
        i++
      }
    },
    onDateSelected: function (unixdate) {
      var date = new PersianDate(unixdate).format('YYYY,MM,DD')
      var fromDate = this.jalaliToTimeStamp(date)
      var toDate = this.jalaliToTimeStamp(date, false)
      this.tests({userUuid: this.sharedTest.user.uuid, fromDate: fromDate, toDate: toDate, type: 'ecg', context: 'daily'})
    },
    onYearSelected: function (year) {
      this.getHighlightedDays()
    },
    onMonthSelected: function (monthIndex) {
      this.getHighlightedDays()
    },
    onNext: function (navigator) {
      this.getHighlightedDays()
    },
    onPrev: function (navigator) {
      this.getHighlightedDays()
    },
    convertData: async function (data, flags) {
      this.disabledForRender = true
      this.$emit('beforeRender')

      this.testStart = data.test_start
      this.testEnd = data.test_end
      var res = await ecgFilterWorker(data, flags, this.testEnd - this.testStart)
      this.testDuration = res.duration
      this.data = res
    },
    checkFilterSettings: function (key, value) {
      switch (key) {
        case 'improvement':
          if (value) {
            this.editing_filter_settings.notch = true
            this.editing_filter_settings.high_pass = true
            this.editing_filter_settings.low_pass = true
          }
          break
        default:
          if (!value) {
            this.editing_filter_settings.improvement = false
          }
          break
      }
    },
    onSelectTest: function (test) {
      this.selectedEcgTest = test
      this.requestData()
    },
    requestData: function () {
      setTimeout(() => {
        var test = this.userECGTest
        if (test) {
          this.testData({uuid: test.uuid, type: 'ecg'})
        }
      }, 1500)
    },
    afterRender: function () {
      this.$emit('afterRender')
      this.disabledForRender = false
      setTimeout(() => {
        this.initDatePicker()
      }, 10)
    },
    renderCalibrate: function () {
      if (this.editing_view_settings.calibrate) {
        if (!this.grid) {
          this.grid = grid()

          this.grid.majorStrokeStyle = 'rgba(191, 191, 191, 255)'
          this.grid.minorStrokeStyle = 'rgba(191, 191, 191, 100)'

          this.grid.majorLineWidth = 0.75
          this.grid.minorLineWidth = 0.25
        }

        const canvas = document.getElementById(`calibrate-canvas`)
        if (!canvas) {
          return
        }
        const devicePixelRatio = window.devicePixelRatio || 1
        canvas.width = canvas.clientWidth * devicePixelRatio
        canvas.height = canvas.clientHeight * devicePixelRatio

        var context = canvas.getContext('2d')
        context.clearRect(0, 0, canvas.width, canvas.height)

        var r = this.editing_view_settings.monitor_ratio.match(/(\d{0,2}):(\d{0,2})/)
        if (r.length < 3) {
          // ratio not valid
          return
        }
        r = r.map(function (e) {
          return parseInt(e, 10)
        })
        let inch = parseInt(this.editing_view_settings.monitor_size)
        let x = Math.sqrt(Math.pow(inch, 2) / (Math.pow(r[1], 2) + Math.pow(r[2], 2)))
        var screenWidthInMm = 16 * x * 25.4
        var pxPerMm = (this.screenWidth / screenWidthInMm) * this.editing_view_settings.calibrate / 100

        this.grid.square(context, 0, 0, canvas.width, canvas.height, 5 * pxPerMm, pxPerMm, null)
      }
    }
  },
  created () {
    this.screenWidth = window.screen.width
    this.screenHeight = window.screen.height

    var that = this
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'test/ecgtestsMinimalSuccess') {
        that.highlightDaysOnDatePicker(mutation.payload)
      }
      if (mutation.type === 'test/ecgTestDataSuccess') {
        this.ecgTestData = mutation.payload.testData

        this.convertData(this.ecgTestData, this.filterFlags)
      }
      if (mutation.type === 'test/ecgTestsSuccess') {
        let tests = mutation.payload
        if (tests.length > 0) {
          this.ecgTestsToSelect = [...tests]
          // select last test to show
          var maxTested = -1
          var lastTest = null
          this.ecgTestsToSelect.forEach(test => {
            if (test.tested_at > maxTested) {
              maxTested = test.tested_at
              lastTest = test
            }
          })
          this.selectedEcgTest = lastTest

          that.requestData()
        }
      }
      if (mutation.type === 'test/ecgTestsFailure') {
        if (mutation.payload.status === 404) {
          notify({
            text: this.$t('more.fetch_error'),
            theme: 'red'
          })
        }
      }
      if (mutation.type === 'test/ecgTestsRequest') {
        this.userECGTest = null
      }
      if (mutation.type === 'test/editTestTagsSuccess') {
        if (this.selectedEcgTest && this.selectedEcgTest.uuid === mutation.payload.testUuid) {
          Vue.set(this.selectedEcgTest, 'tags', mutation.payload.tags)
        }
      }
    })
  },
  mounted () {
    this.view_settings = JSON.parse(localStorage.getItem('view_settings')) || {
      monitor_size: '15',
      monitor_ratio: '16:10',
      calibrate: 100
    }

    this.filter_settings = JSON.parse(localStorage.getItem('filter_settings')) || {
      chart_height: 120,
      scale_x: 2,
      scale_y: 2,
      notch: true,
      low_pass: true,
      high_pass: true,
      improvement: true
    }

    if (this.filter_settings.hasOwnProperty('chart_height')) {
      this.lazy_chart_height = this.filter_settings.chart_height
    }

    this.editing_filter_settings = {...this.filter_settings}
    this.editing_view_settings = {...this.view_settings}
    this.initDatePicker()

    if (this.sharedTest && this.sharedTest.last_tests && this.sharedTest.last_tests.length > 0) {
      var hrTest = this.sharedTest.last_tests.filter(test => {
        return test.type === 'ecg'
      })
      this.ecgTestsToSelect = hrTest

      // select last test to show
      var maxTested = -1
      var lastTest = null
      this.ecgTestsToSelect.forEach(test => {
        if (test.tested_at > maxTested) {
          maxTested = test.tested_at
          lastTest = test
        }
      })
      this.selectedEcgTest = lastTest
    }
    this.requestData()
  },
  destroyed () {
    this.unsubscribe()
  }
}
</script>

<style>
.hrresult {
  text-align: right;
}
.hrresult-tags {
  text-align: start;
}
.hr-result-container {
  /* fix margin on display flex container */
  padding-bottom: 1em;
  text-align: center;
}
.hr-result-container .dropdown-menu {
  max-height:400px;
  overflow-y:auto;
}
.filter-desc {
  font-size: smaller;
  color: gray;
  margin: 0 10px 0;
}
#modal-2 {
  text-align: right;
}
.ecgresult-title {
  color: var(--blood-pressure-text-color);
  font-size: small;
}
.ecgresult-value {
  margin: 0 10px 0;
  font-weight: bold;
  color: var(--blood-pressure-value-color);
}
.chart-height-message-container {
  padding-top: 2.5em;
  padding-bottom: 2.5em;
  text-align: center;
}
.chart-height-message {
  display: block;
  font-size: 2.5em;
  color: var(--error-icon-color);
}
.chart-height-message-caption {
  color: var(--error-text-color);
}
.setting-tabs a {
  padding: 0.5rem !important;
}
.setting-tabs a:hover, .setting-tabs .active {
    background: #fff !important;
    color: #000 !important;
}
.setting-tabs .card-header{
  padding: 0.1 !important;
}
.form-view-combo {
  padding-right: 0 !important;
}
#calibrate-canvas {
  border: 1px solid rgba(191, 191, 191, 255);
}
.calibrate-tip {
  background-color: var(--calibrate-tip-bg-color);
  border: 1px solid var(--calibrate-tip-border-color);
  border-radius: 3px;
  line-height: 3em;
}
.calibrate-tip .icon {
  margin: 0 10px 0;
  color: var(--calibrate-tip-border-color);
}
</style>



// WEBPACK FOOTER //
// src/views/HeartRateResult.vue